import { Card, Col, Row } from 'antd';
import useFormatDate from '../../../../hooks/format/useFormatDate';
import { useTranslation } from 'react-i18next';
import useNumberFormatter from '../../../../hooks/format/useNumberFormatter';
import React, { useEffect, useRef, useState } from 'react';
import useIsUiHidden from '../../../../hooks/dom/useIsUiHidden';
import ExpandableButton from '../../ExpandableButton';
import { ReactComponent as ExpertIcon } from 'assets/img/icons/cardIcons/expert.svg';
import { InfoCircleOutlined } from '@ant-design/icons';
import CollapsibleTable from '../../CollapsibleTable';
import CompanyDataCard from '../../CompanyDataCard';

const SibgleRecordBlock = ({ limit, date, explanation = false, pdfAlign = false }) => {
  const [hovered, setHovered] = useState(false);
  const { t } = useTranslation();

  const hoverTimer = useRef(null);

  const handleMouseEnter = () => {
    if (explanation) {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      hoverTimer.current = setTimeout(() => {
        setHovered(true);
      }, 150);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
    }
    setHovered(false);
  };

  useEffect(() => {
    return () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    };
  }, []);

  const Explanation = ({ explanation }) => (
    <Row style={{ height: '100%' }}>
      <Col
        span={24}
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'start',
          justifyContent: 'start',
          fontWeight: '400',
          fontSize: 12,
        }}
      >
        <div className="title">{explanation}</div>
      </Col>
    </Row>
  );

  return (
    <Col span={pdfAlign ? 8 : 6} order={2}>
      <Card
        className={`card-simple card-simple-padding-none has-shadow`}
        style={{ height: '100%', margin: 'auto', padding: 0, position: 'relative', overflow: 'hidden' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {explanation && !pdfAlign && (
          <InfoCircleOutlined className="icon--gray" style={{ position: 'absolute', top: 5, right: 5, zIndex: 2 }} />
        )}
        {hovered ? (
          <Explanation explanation={explanation} />
        ) : (
          <>
            <Row>
              {/* Left Column */}
              <Col
                span={14}
                style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'start',
                  justifyContent: 'start',
                  fontWeight: '400',
                  fontSize: 14,
                }}
                className="recommendedLimitTitle"
              >
                <div>{t('professionalReport.financialData.expertEvaluation.recommendedLimit')}</div>
              </Col>
              <Col
                span={10}
                style={{
                  display: 'flex',
                  alignItems: 'end',
                  justifyContent: 'start',
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                <div>{limit}</div>
              </Col>
              <Col
                span={14}
                style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'start',
                  justifyContent: 'start',
                  fontWeight: '400',
                  fontSize: 12,
                }}
              >
                <div>{t('professionalReport.financialData.expertEvaluation.evaluationDate')}</div>
              </Col>
              <Col
                span={10}
                style={{
                  display: 'flex',
                  alignItems: 'end',
                  justifyContent: 'start',
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                <div>{date}</div>
              </Col>
              {/* Right Columns */}
            </Row>
          </>
        )}
      </Card>
    </Col>
  );
};

const ExpertEvalutationTable = ({ expertEvaluations }) => {
  const { t } = useTranslation();
  const isUiHidden = useIsUiHidden();
  const formatDate = useFormatDate();
  const formatNumber = useNumberFormatter({ style: 'decimal' });

  const columns = [
    {
      title: t('professionalReport.financialData.expertEvaluation.evaluationDate'),
      dataIndex: 'evaluationDate',
      render: value => formatDate(value, 'P'),
      key: 'evaluationDate',
      sorter: (a, b) => {
        const dateA = new Date(a.evaluationDate);
        const dateB = new Date(b.evaluationDate);
        return dateB - dateA;
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: t('professionalReport.financialData.expertEvaluation.recommendedLimit'),
      dataIndex: 'recommendedLimit',
      key: 'recommendedLimit',
      render: (value, record) => `${formatNumber(value)} ${record.currency}`,
      sorter: (a, b) => a.recommendedLimit - b.recommendedLimit,
      sortDirections: ['ascend', 'descend'],
    },
  ];

  const expandedRowRender = record => (
    <div>
      <p>{record.description}</p>
    </div>
  );

  if (!expertEvaluations?.length) {
    return null;
  }

  return (
    <CompanyDataCard title={t('professionalReport.financialData.expertEvaluation')} icon={<ExpertIcon />}>
      <Row gutter={[16]}>
        {expertEvaluations?.slice(0, isUiHidden ? 3 : 4)?.map(item => (
          <SibgleRecordBlock
            limit={`${formatNumber(item.recommendedLimit)} ${item.currency}`}
            date={formatDate(item.evaluationDate, 'P')}
            explanation={item.description}
            pdfAlign={isUiHidden}
          />
        ))}
      </Row>
      {isUiHidden || expertEvaluations?.length > 4 ? (
        <CollapsibleTable
          title={t('global.detailedInformation')}
          className="space-top-md"
          rowKey="evaluationDate"
          data={expertEvaluations.map(item => ({
            ...item,
            key: item.evaluationDate,
          }))}
          columns={columns}
          expandable={{
            rowExpandable: record => record.description,
            expandedRowRender,
            defaultExpandAllRows: isUiHidden,
            expandIcon: ({ expanded, onExpand, record }) =>
              record.description && <ExpandableButton onClick={e => onExpand(record, e)} isActive={expanded} />,
          }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          showSorterTooltip={false}
        />
      ) : null}
    </CompanyDataCard>
  );
};

export default ExpertEvalutationTable;
